<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { endpoints, type GroupResponse } from '@/api'
import { ApiError } from '@/api/client-exports'

/**
 * Group settings (SPEC §6.4, §8.1).
 *
 * Everything the creation wizard deliberately skipped lives here. Changing the
 * time zone changes how times are shown and nothing else — stored instants are
 * UTC and are not rewritten, so a meal that happened at noon still happened
 * when it happened.
 */

const route = useRoute()
const groupId = route.params.groupId as string

const group = ref<GroupResponse | null>(null)
const zones = ref<string[]>([])

const loading = ref(true)
const saving = ref(false)
const saved = ref(false)
const error = ref<string | null>(null)
const fieldErrors = ref<Record<string, string[]>>({})

const form = ref({
  name: '',
  timeZoneId: '',
  description: '',
  defaultLocationName: '',
  defaultLocationAddress: '',
  defaultStartLocalTime: '',
  defaultRsvpCutoffHours: null as number | null,
  childServingFactor: 0.6,
  householdVisibility: 'Named',
  allowHouseholdRename: true,
  eventShareVisibility: 'MenuAndCounts',
  eventCompletionDelayHours: 12,
})

const householdVisibilities = [
  { value: 'Named', title: 'Named — households see each other' },
  { value: 'Anonymous', title: 'Anonymous — counts only' },
]

const shareVisibilities = [
  { value: 'MenuOnly', title: 'Menu only' },
  { value: 'MenuAndCounts', title: 'Menu and counts' },
  { value: 'MenuAndHouseholds', title: 'Menu and households' },
]

onMounted(async () => {
  const supported = Intl.supportedValuesOf?.('timeZone') ?? []

  try {
    const loaded = await endpoints.getGroup(groupId)
    group.value = loaded

    // The group's own zone is prepended when the platform list omits it, for
    // the same reason as on the creation screen: otherwise a group already set
    // to such a zone could not be saved without silently changing it.
    zones.value = supported.includes(loaded.timeZoneId)
      ? supported
      : [loaded.timeZoneId, ...supported]

    form.value = {
      name: loaded.name,
      timeZoneId: loaded.timeZoneId,
      description: loaded.description ?? '',
      defaultLocationName: loaded.defaultLocationName ?? '',
      defaultLocationAddress: loaded.defaultLocationAddress ?? '',
      defaultStartLocalTime: loaded.defaultStartLocalTime ?? '',
      defaultRsvpCutoffHours: toNumber(loaded.defaultRsvpCutoffHours),
      childServingFactor: toNumber(loaded.childServingFactor) ?? 0.6,
      householdVisibility: loaded.householdVisibility,
      allowHouseholdRename: loaded.allowHouseholdRename,
      eventShareVisibility: loaded.eventShareVisibility,
      eventCompletionDelayHours: toNumber(loaded.eventCompletionDelayHours) ?? 12,
    }
  } catch (cause) {
    error.value =
      cause instanceof ApiError && cause.status === 404
        ? 'That group is not one of yours.'
        : 'We could not load these settings.'
  } finally {
    loading.value = false
  }
})

/**
 * Coerces a numeric field from the wire.
 *
 * The generated types say `number | string`, because the OpenAPI document
 * permits either for numeric formats. Rather than spreading that union through
 * the form, it is resolved once here, at the boundary where it arrives.
 */
function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function errorFor(field: string): string | undefined {
  const key = Object.keys(fieldErrors.value).find((k) => k.toLowerCase() === field.toLowerCase())
  return key ? fieldErrors.value[key]?.[0] : undefined
}

async function save(): Promise<void> {
  error.value = null
  fieldErrors.value = {}
  saved.value = false
  saving.value = true

  try {
    group.value = await endpoints.updateGroup(groupId, {
      name: form.value.name,
      timeZoneId: form.value.timeZoneId,
      description: form.value.description,
      defaultLocationName: form.value.defaultLocationName,
      defaultLocationAddress: form.value.defaultLocationAddress,
      defaultStartLocalTime: form.value.defaultStartLocalTime,
      defaultRsvpCutoffHours: form.value.defaultRsvpCutoffHours,
      childServingFactor: form.value.childServingFactor,
      householdVisibility: form.value.householdVisibility,
      allowHouseholdRename: form.value.allowHouseholdRename,
      eventShareVisibility: form.value.eventShareVisibility,
      eventCompletionDelayHours: form.value.eventCompletionDelayHours,
    })

    saved.value = true
  } catch (cause) {
    if (cause instanceof ApiError && cause.problem?.errors) {
      fieldErrors.value = cause.problem.errors
    } else {
      error.value =
        cause instanceof ApiError ? cause.userMessage : 'Something went wrong. Try again.'
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <v-container class="py-10" style="max-width: 36rem">
    <h1 class="text-h5 mb-6">Group settings</h1>

    <div v-if="loading" class="py-8 text-body-2" style="opacity: 0.75" role="status">Loading…</div>

    <div
      v-else-if="error && !group"
      data-testid="settings-load-error"
      role="alert"
      class="pa-3 rounded text-body-2"
      style="background-color: rgb(var(--v-theme-error)); color: rgb(var(--v-theme-on-error))"
    >
      {{ error }}
    </div>

    <template v-else>
      <div
        v-if="error"
        data-testid="settings-error"
        role="alert"
        class="mb-4 pa-3 rounded text-body-2"
        style="background-color: rgb(var(--v-theme-error)); color: rgb(var(--v-theme-on-error))"
      >
        {{ error }}
      </div>

      <div
        v-if="saved"
        data-testid="settings-saved"
        role="status"
        class="mb-4 pa-3 rounded text-body-2"
        style="background-color: rgb(var(--v-theme-surface-variant))"
      >
        Settings saved.
      </div>

      <v-form novalidate @submit.prevent="save">
        <v-text-field
          v-model="form.name"
          label="Group name"
          name="name"
          :error-messages="errorFor('name')"
          :disabled="saving"
          class="mb-2"
        />

        <v-textarea
          v-model="form.description"
          label="Description"
          name="description"
          rows="2"
          :disabled="saving"
          class="mb-2"
        />

        <v-autocomplete
          v-model="form.timeZoneId"
          :items="zones"
          label="Time zone"
          name="timeZoneId"
          hint="Changes how times are shown. Meals already planned keep the moment they were set for."
          persistent-hint
          :error-messages="errorFor('timeZoneId')"
          :disabled="saving"
          class="mb-4"
        />

        <h2 class="text-subtitle-1 mt-6 mb-2">Defaults for new meals</h2>

        <v-text-field
          v-model="form.defaultLocationName"
          label="Usual place"
          name="defaultLocationName"
          :disabled="saving"
          class="mb-2"
        />

        <v-text-field
          v-model="form.defaultLocationAddress"
          label="Address"
          name="defaultLocationAddress"
          :disabled="saving"
          class="mb-2"
        />

        <v-text-field
          v-model="form.defaultStartLocalTime"
          label="Usual start time"
          name="defaultStartLocalTime"
          type="time"
          :error-messages="errorFor('defaultStartLocalTime')"
          :disabled="saving"
          class="mb-2"
        />

        <v-text-field
          v-model.number="form.defaultRsvpCutoffHours"
          label="RSVPs close this many hours before"
          name="defaultRsvpCutoffHours"
          type="number"
          min="0"
          :error-messages="errorFor('defaultRsvpCutoffHours')"
          :disabled="saving"
          class="mb-2"
        />

        <v-text-field
          v-model.number="form.childServingFactor"
          label="A child's share of an adult portion"
          name="childServingFactor"
          type="number"
          step="0.05"
          min="0.05"
          max="1"
          hint="Only used for the estimate shown before people say what they are bringing."
          persistent-hint
          :error-messages="errorFor('childServingFactor')"
          :disabled="saving"
          class="mb-4"
        />

        <h2 class="text-subtitle-1 mt-6 mb-2">What people can see</h2>

        <v-select
          v-model="form.householdVisibility"
          :items="householdVisibilities"
          label="Households"
          name="householdVisibility"
          :error-messages="errorFor('householdVisibility')"
          :disabled="saving"
          class="mb-2"
        />

        <!--
          A family renaming themselves is a small correction they should not
          have to ask for: an organizer types "Dace Family" while setting the
          group up, and the family know whether they are "The Daces"
          (SPEC §8.8). Permissive by default, and switchable by whoever wants
          the list to read one way.
        -->
        <v-checkbox
          v-model="form.allowHouseholdRename"
          label="Let families change their own household name"
          name="allowHouseholdRename"
          :disabled="saving"
          density="comfortable"
          hide-details
          class="mb-2"
        />

        <v-select
          v-model="form.eventShareVisibility"
          :items="shareVisibilities"
          label="Shared meal links show"
          name="eventShareVisibility"
          :error-messages="errorFor('eventShareVisibility')"
          :disabled="saving"
          class="mb-2"
        />

        <v-text-field
          v-model.number="form.eventCompletionDelayHours"
          label="Hours after a meal before it is marked done"
          name="eventCompletionDelayHours"
          type="number"
          min="0"
          :error-messages="errorFor('eventCompletionDelayHours')"
          :disabled="saving"
          class="mb-6"
        />

        <v-btn
          type="submit"
          color="primary"
          size="large"
          block
          :loading="saving"
          :disabled="saving"
        >
          Save settings
        </v-btn>
      </v-form>

      <!--
        Linked from here rather than given a place in the navigation: it is
        visited to answer a question, not as part of running a meal, and a sixth
        item in a phone's bottom bar costs every other one its room (MT-110).
      -->
      <v-divider class="my-8" />
      <v-list-item
        :to="{ name: 'group-audit', params: { groupId } }"
        prepend-icon="mdi-history"
        append-icon="mdi-chevron-right"
        title="Activity"
        subtitle="Who changed what, and when"
        data-testid="settings-audit-link"
      />

      <!--
        Reminders are group settings with a screen of their own, because each
        of the four is a small form (MT-156).
      -->
      <v-divider class="my-4" />
      <v-list-item
        :to="{ name: 'group-reminders', params: { groupId } }"
        prepend-icon="mdi-bell-outline"
        append-icon="mdi-chevron-right"
        title="Reminders"
        subtitle="When families are nudged, and which ones"
        data-testid="settings-reminders-link"
      />
    </template>
  </v-container>
</template>
